import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { auditAPI } from '../utils/api';
import { formatDateTime } from '../utils/helpers';
import { FileText, User, Search, Download, Eye, X, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const ACTION_COLORS = {
  CREATE: 'text-green-600 bg-green-50',
  UPDATE: 'text-blue-600 bg-blue-50',
  DELETE: 'text-red-600 bg-red-50',
  APPROVE: 'text-green-600 bg-green-50',
  REJECT: 'text-red-600 bg-red-50',
  LOCK: 'text-slate-600 bg-slate-50',
  RESOLVE: 'text-emerald-600 bg-emerald-50',
  RFQ_CONVERT: 'text-violet-600 bg-violet-50',
  SEND: 'text-blue-600 bg-blue-50',
};

const RESOURCE_TYPES = [
  'all', 'proposal', 'requirement', 'configuration', 'prospect',
  'site', 'alarm', 'rfq', 'billing', 'user',
];

export const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState('');
  const [filterResourceType, setFilterResourceType] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [detailLog, setDetailLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [filterResourceType, filterAction]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterResourceType !== 'all') params.resource_type = filterResourceType;
      if (filterAction !== 'all') params.action = filterAction;
      if (searchId.trim()) params.resource_id = searchId.trim();
      const response = await auditAPI.getLogs(200, params);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchLogs();

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (filterResourceType !== 'all') params.resource_type = filterResourceType;
      if (filterAction !== 'all') params.action = filterAction;
      if (searchId.trim()) params.resource_id = searchId.trim();
      const response = await auditAPI.exportCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit_logs.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const handleExportJSON = async () => {
    try {
      const params = {};
      if (filterResourceType !== 'all') params.resource_type = filterResourceType;
      if (filterAction !== 'all') params.action = filterAction;
      if (searchId.trim()) params.resource_id = searchId.trim();
      const response = await auditAPI.exportJSON(params);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit_logs.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON exported');
    } catch {
      toast.error('Failed to export JSON');
    }
  };

  // Get unique actions from logs for filter
  const uniqueActions = ['all', ...new Set(logs.map(l => l.action).filter(Boolean))];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <PageHeader
            title="Audit Logs"
            description="Track all system changes and user actions"
          />

          {/* Filters + Export bar */}
          <div className="flex flex-wrap items-end gap-3 mb-5">
            {/* Search by resource_id */}
            <div className="flex-1 min-w-[200px] max-w-sm">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Search Resource ID</label>
              <div className="relative flex gap-1">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchId}
                    onChange={e => setSearchId(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    placeholder="Search resource_id..."
                    className="pl-9 h-9 text-sm"
                    data-testid="audit-search-resource-id"
                  />
                </div>
                <Button size="sm" variant="outline" className="h-9" onClick={handleSearch}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Resource type filter */}
            <div className="w-44">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Resource Type</label>
              <Select value={filterResourceType} onValueChange={setFilterResourceType}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-resource-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map(rt => (
                    <SelectItem key={rt} value={rt}>{rt === 'all' ? 'All Types' : rt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Action filter */}
            <div className="w-40">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Action</label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="h-9 text-sm" data-testid="audit-filter-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {uniqueActions.map(a => (
                    <SelectItem key={a} value={a}>{a === 'all' ? 'All Actions' : a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Export buttons */}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleExportCSV} data-testid="audit-export-csv">
                <Download className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
              <Button size="sm" variant="outline" onClick={handleExportJSON} data-testid="audit-export-json">
                <Download className="w-3.5 h-3.5 mr-1" /> JSON
              </Button>
            </div>
          </div>

          {/* Tooltip explaining resource_id */}
          <div className="mb-4 flex items-center gap-1.5 text-[10px] text-slate-400">
            <Info className="w-3 h-3" />
            <span>resource_id = unique identifier of the affected record (e.g., proposal, site, alarm).</span>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
              {logs.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No audit logs"
                  description="System activity will be recorded here"
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Timestamp</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">User</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Action</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Resource Type</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Resource ID</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr
                          key={log.log_id}
                          data-testid={`audit-log-${log.log_id}`}
                          className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors"
                        >
                          <td className="px-4 py-3 text-slate-900 font-mono text-xs whitespace-nowrap">
                            {formatDateTime(log.timestamp)}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs">
                            <div className="flex items-center space-x-2">
                              <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <span className="truncate max-w-[160px]">{log.user_email}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${ACTION_COLORS[log.action] || 'text-slate-600 bg-slate-50'}`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 capitalize text-xs">{log.resource_type}</td>
                          <td className="px-4 py-3 text-slate-600 font-mono text-xs">
                            <span title={log.resource_id} className="cursor-help">
                              {log.resource_id?.substring(0, 12)}...
                            </span>
                          </td>
                          <td className="px-2 py-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => setDetailLog(log)}
                              data-testid={`audit-view-${log.log_id}`}
                            >
                              <Eye className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Log Detail Modal */}
      <Dialog open={!!detailLog} onOpenChange={() => setDetailLog(null)}>
        <DialogContent className="max-w-lg" data-testid="audit-detail-modal">
          <DialogHeader>
            <DialogTitle>Log Details</DialogTitle>
          </DialogHeader>
          {detailLog && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Timestamp</p>
                  <p className="text-slate-700 font-mono text-xs mt-0.5">{formatDateTime(detailLog.timestamp)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Actor</p>
                  <p className="text-slate-700 text-xs mt-0.5">{detailLog.user_email}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Action</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${ACTION_COLORS[detailLog.action] || 'text-slate-600 bg-slate-50'}`}>
                    {detailLog.action}
                  </span>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Resource Type</p>
                  <p className="text-slate-700 text-xs capitalize mt-0.5">{detailLog.resource_type}</p>
                </div>
              </div>

              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">Resource ID (full)</p>
                <p className="text-slate-700 font-mono text-xs mt-0.5 bg-slate-50 rounded px-2 py-1 break-all" data-testid="audit-detail-resource-id">
                  {detailLog.resource_id}
                </p>
              </div>

              {detailLog.ip_address && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">IP Address</p>
                  <p className="text-slate-700 font-mono text-xs mt-0.5">{detailLog.ip_address}</p>
                </div>
              )}

              {detailLog.changes && Object.keys(detailLog.changes).length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Before / After Diff</p>
                  <div className="bg-slate-50 rounded p-3 text-xs font-mono overflow-auto max-h-[300px]">
                    <pre className="text-slate-700 whitespace-pre-wrap">{JSON.stringify(detailLog.changes, null, 2)}</pre>
                  </div>
                </div>
              )}

              {(!detailLog.changes || Object.keys(detailLog.changes).length === 0) && (
                <div className="text-xs text-slate-400 italic bg-slate-50 rounded p-3">
                  No before/after diff available for this log entry.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
